/* Warm Wonders pillow collection. Product data stays in Shopify; no catalog writes. */
if (!customElements.get('ww-pillow-shop')) {
  customElements.define('ww-pillow-shop', class extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.grid = this.querySelector('.ww-grid');
      this.notice = this.querySelector('.ww-filter-status');
      this.pageSize = Number(this.dataset.pageSize) || 16;
      this.storageKey = 'warmwonders:pillow-wishlist:v1';
      this.saved = new Set();
      this.storageOK = true;
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        if (Array.isArray(saved)) this.saved = new Set(saved.map(String));
      } catch (_) { this.storageOK = false; }
      this.styles = this.parse(this.dataset.styles).map(value => value.trim());
      this.colors = this.parse(this.dataset.colors).map(value => value.trim());
      if (this.normalizeStyleRoute()) return;
      this.readState();
      const styleRow = this.querySelector('.ww-categories');
      if (styleRow) {
        const track = document.createElement('div');
        track.className = 'ww-style-scroll-track';
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('span');
        track.append(thumb);
        styleRow.after(track);
        const updateTrack = () => {
          const total = styleRow.scrollWidth || 1;
          thumb.style.width = Math.min(100, 100 * styleRow.clientWidth / total) + '%';
          thumb.style.marginLeft = (100 * Math.max(0, styleRow.scrollLeft) / total) + '%';
        };
        styleRow.addEventListener('scroll', updateTrack, {passive:true, signal:this.abort.signal});
        this.styleRowObserver = new ResizeObserver(updateTrack);
        this.styleRowObserver.observe(styleRow);
        updateTrack();
      }
      this.bindCart();
      this.querySelector('.ww-sort-form').addEventListener('change', event => {
        if (event.target.name !== 'sort_by') return;
        const url = new URL(location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        location.assign(url.href);
      });
      this.querySelector('.ww-hero__button')?.addEventListener('click', event => {
        event.preventDefault();
        this.querySelector('.ww-shopping').scrollIntoView({behavior: this.motion(), block: 'start'});
      });
      try {
        await this.loadCollection();
        if (!this.isConnected) return;
        this.buildFilters();
        this.bindFilters();
        this.querySelector('.ww-filter-controls').hidden = false;
        this.querySelector('.ww-native-pagination').hidden = true;
        this.querySelectorAll('[data-ww-heart]').forEach(button => { button.hidden = !this.storageOK; });
        this.querySelector('.ww-saved-toggle').hidden = !this.storageOK;
        this.render(false, false);
      } catch (error) {
        if (error.name !== 'AbortError') this.notice.textContent = 'Advanced filters could not load. You can still browse the collection or refresh to try again.';
      }
    }
    disconnectedCallback() { this.styleRowObserver?.disconnect(); this.abort?.abort(); window.removeEventListener('popstate', this.onPop); window.removeEventListener('storage', this.onStorage); }
    parse(value) { try { const parsed = JSON.parse(value || '[]'); return Array.isArray(parsed) ? parsed : []; } catch (_) { return []; } }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    readState() {
      const query = new URLSearchParams(location.search);
      this.state = {size: query.get('ww_size') || '', color: query.get('ww_color') || '', style: this.canonicalStyle(query.get('ww_style') || ''), available: query.get('ww_available') === '1', min: query.get('ww_min') || '', max: query.get('ww_max') || '', saved: query.get('ww_saved') === '1'};
      this.page = Math.max(1, Number(query.get('page')) || 1);
    }
    async loadCollection() {
      const pages = Math.max(1, Number(this.dataset.pages));
      const currentPage = Number(this.dataset.page) || 1;
      const byPage = new Map([[currentPage, [...this.querySelectorAll('[data-ww-card]')]]]);
      const queue = Array.from({length: pages}, (_, index) => index + 1).filter(page => page !== currentPage);
      if (queue.length) this.notice.textContent = 'Preparing collection filters…';
      const worker = async () => {
        while (queue.length) {
          const page = queue.shift();
          const url = new URL(location.href);
          url.searchParams.set('page', page);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, {signal: this.abort.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Collection unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const shop = doc.querySelector('ww-pillow-shop');
          if (!shop || Number(shop.dataset.total) !== Number(this.dataset.total)) throw new Error('Collection updated while loading');
          byPage.set(page, [...shop.querySelectorAll('[data-ww-card]')]);
        }
      };
      await Promise.all(Array.from({length: Math.min(3, queue.length)}, worker));
      const unique = new Map();
      [...byPage.entries()].sort((a,b) => a[0] - b[0]).forEach(([,cards]) => cards.forEach(card => unique.set(card.dataset.productId, card)));
      if (unique.size !== Number(this.dataset.total)) throw new Error('Incomplete collection');
      this.cards = [...unique.values()].map(element => ({element, id: element.dataset.productId, tags: this.parse(element.dataset.tags), style: this.primaryStyle(this.parse(element.dataset.tags)), sizes: this.parse(element.dataset.sizes), available: element.dataset.available === 'true', price: Number(element.dataset.price)}));
      this.notice.textContent = '';
    }
    // Assign one of the six exact style tags in Shopify. Descriptive tags stay independent.
    primaryStyle(tags) {
      const assigned = this.styles.find(style => tags.some(tag => tag.trim().toLowerCase() === style.toLowerCase()));
      if (assigned) return assigned;
      const seasonal = /^(seasonal|christmas|halloween|fall|autumn|thanksgiving|holiday|holidays)$/i;
      if (tags.some(tag => seasonal.test(tag.trim()))) return 'Seasonal';
      return '';
    }
    canonicalStyle(value) {
      const key = value.trim().toLowerCase();
      const aliases = {victorian:'Victorian & Vintage', floral:'Cozy Cottage', botanical:'Earthy Botanical', nature:'Earthy Botanical', striped:'Modern Farmhouse', stripes:'Modern Farmhouse', gingham:'Modern Farmhouse', checks:'Modern Farmhouse'};
      return aliases[key] || this.styles.find(style => style.toLowerCase() === key || style.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') === key) || '';
    }
    normalizeStyleRoute() {
      const base = this.dataset.collectionUrl.replace(/\/$/, '');
      const path = location.pathname.replace(/\/$/, '');
      if (!path.startsWith(base + '/')) return false;
      const style = this.canonicalStyle(decodeURIComponent(path.slice(base.length + 1)));
      if (!style) return false;
      const url = new URL(location.href);
      url.pathname = base;
      url.searchParams.set('ww_style', style);
      url.searchParams.delete('page');
      location.replace(url.href);
      return true;
    }
    buildFilters() {
      const existingTags = new Set(this.cards.flatMap(card => card.tags));
      const sizes = [...new Set(this.cards.flatMap(card => card.sizes))];
      const choices = {size: sizes, color: this.colors.filter(color => existingTags.has(color)), style: this.styles};
      Object.entries(choices).forEach(([type, values]) => {
        const select = this.querySelector(`[data-ww-filter="${type}"]`);
        values.forEach(value => select.add(new Option(type === 'size' ? value.replaceAll('"', '') + ' in' : value, value)));
        select.closest('label').hidden = !values.length;
      });
      this.querySelectorAll('[data-ww-style]').forEach(link => {
        const style = link.dataset.wwStyle;
        if (!style) return;
        const card = this.cards.find(card => card.style === style);
        const soon = link.querySelector('[data-ww-soon]');
        if (soon) soon.hidden = Boolean(card);
        if (!card) return;
        if (link.dataset.customImage !== 'true') {
          const source = card.element.querySelector('.ww-card__primary');
          let target = link.querySelector('img');
          if (source && !target) {
            target = document.createElement('img'); target.alt = ''; target.loading = 'lazy';
            link.querySelector('.ww-category__image').replaceChildren(target);
          }
          if (source && target) {
            link.querySelector('.ww-category__image').classList.add('ww-category__image--product');
            const url = new URL(source.getAttribute('src'), location.origin);
            url.searchParams.set('width', '900');
            target.src = url.href;
            target.removeAttribute('srcset');
          }
        }
      });
    }
    bindFilters() {
      this.querySelectorAll('[data-ww-filter]').forEach(select => select.addEventListener('change', () => { this.state[select.dataset.wwFilter] = select.value; this.render(true); }));
      this.querySelectorAll('[data-ww-style]').forEach(link => link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        // Native tag URLs remain valid without JavaScript. Reset a tag-scoped page normally.
        if (location.pathname.replace(/\/$/, '') !== this.dataset.collectionUrl.replace(/\/$/, '')) return;
        event.preventDefault();
        if (link.classList.contains('ww-view-all')) Object.keys(this.state).forEach(key => { this.state[key] = typeof this.state[key] === 'boolean' ? false : ''; });
        else this.state.style = link.dataset.wwStyle;
        this.render(true);
      }));
      this.querySelector('.ww-filter-toggle').addEventListener('click', event => {
        const button = event.currentTarget;
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        this.querySelector('.ww-extra').hidden = !expanded;
      });
      this.querySelector('[data-ww-available]').addEventListener('change', event => { this.state.available = event.target.checked; this.render(true); });
      ['min', 'max'].forEach(type => this.querySelector(`[data-ww-${type}]`).addEventListener('change', event => { this.state[type] = event.target.value; this.render(true); }));
      this.querySelector('.ww-saved-toggle').addEventListener('click', () => { this.state.saved = !this.state.saved; this.render(true); });
      this.addEventListener('click', event => {
        const heart = event.target.closest('[data-ww-heart]');
        if (heart) {
          const card = heart.closest('[data-ww-card]');
          const id = card.dataset.productId;
          const next = new Set(this.saved);
          next.has(id) ? next.delete(id) : next.add(id);
          try { localStorage.setItem(this.storageKey, JSON.stringify([...next])); this.saved = next; this.render(false, false); }
          catch (_) { this.notice.textContent = 'This browser could not save favorites. Please allow local site storage and try again.'; }
        }
        const chip = event.target.closest('[data-ww-remove]');
        if (chip) { const key = chip.dataset.wwRemove; this.state[key] = typeof this.state[key] === 'boolean' ? false : ''; this.render(true); }
        if (event.target.closest('[data-ww-clear]')) {
          Object.keys(this.state).forEach(key => { this.state[key] = typeof this.state[key] === 'boolean' ? false : ''; });
          this.render(true);
        }
      });
      this.onPop = () => { this.readState(); this.render(false, false); };
      window.addEventListener('popstate', this.onPop);
      this.onStorage = event => { if (event.key === this.storageKey) { this.saved = new Set(this.parse(event.newValue).map(String)); this.render(false, false); } };
      window.addEventListener('storage', this.onStorage);
    }
    render(resetPage, updateURL = true) {
      if (resetPage) this.page = 1;
      const state = this.state;
      const matching = this.cards.filter(card => (!state.size || card.sizes.includes(state.size)) && (!state.color || card.tags.includes(state.color)) && (!state.style || card.style === state.style) && (!state.available || card.available) && (!state.saved || this.saved.has(card.id)) && (state.min === '' || card.price >= Number(state.min) * 100) && (state.max === '' || card.price <= Number(state.max) * 100));
      const pages = Math.max(1, Math.ceil(matching.length / this.pageSize));
      this.page = Math.min(this.page, pages);
      const visible = matching.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
      const nextElements = visible.map(card => card.element);
      const currentElements = [...this.grid.children];
      if (currentElements.length !== nextElements.length || currentElements.some((element,index) => element !== nextElements[index])) this.grid.replaceChildren(...nextElements);
      this.cards.forEach(card => {
        const heart = card.element.querySelector('[data-ww-heart]');
        heart.hidden = !this.storageOK;
        heart.setAttribute('aria-pressed', String(this.saved.has(card.id)));
        const title = card.element.querySelector('.ww-card__title').textContent.trim();
        heart.setAttribute('aria-label', `${this.saved.has(card.id) ? 'Unsave' : 'Save'} ${title}`);
      });
      this.querySelector('.ww-count').textContent = `${matching.length} ${matching.length === 1 ? 'product' : 'products'}`;
      this.querySelector('.ww-empty').hidden = matching.length > 0;
      const ranchComingSoon = state.style === 'Western Ranch' && !this.cards.some(card => card.style === 'Western Ranch');
      this.querySelector('.ww-empty h2').textContent = ranchComingSoon ? 'Western Ranch is on its way.' : 'No pillows match just yet.';
      this.querySelector('.ww-empty p').textContent = ranchComingSoon ? 'Warm earth tones and heritage-inspired patterns. Explore our other styles while this collection takes shape.' : 'Try a different color, size, or style to find your little touch of warmth.';
      this.querySelector('.ww-empty [data-ww-clear]').textContent = ranchComingSoon ? 'Explore available styles' : 'Clear all filters';
      this.querySelectorAll('[data-ww-filter]').forEach(select => { select.value = state[select.dataset.wwFilter]; });
      this.querySelector('[data-ww-available]').checked = state.available;
      this.querySelector('[data-ww-min]').value = state.min;
      this.querySelector('[data-ww-max]').value = state.max;
      this.querySelectorAll('[data-ww-style]').forEach(link => link.setAttribute('aria-current', String(link.dataset.wwStyle === state.style)));
      this.querySelector('[data-ww-saved-count]').textContent = this.cards.filter(card => this.saved.has(card.id)).length;
      this.querySelector('.ww-saved-toggle').setAttribute('aria-pressed', String(state.saved));
      this.querySelector('.ww-saved-note').hidden = !state.saved;
      const chips = this.querySelector('.ww-active');
      chips.replaceChildren();
      Object.entries(state).forEach(([key, value]) => {
        if (!value) return;
        const button = document.createElement('button'); button.type = 'button'; button.dataset.wwRemove = key;
        const label = key === 'available' ? 'In stock' : key === 'saved' ? 'Saved' : key === 'min' ? `Min: ${value}` : key === 'max' ? `Max: ${value}` : String(value);
        button.textContent = `${label} ×`; button.setAttribute('aria-label', `Remove ${label} filter`); chips.append(button);
      });
      chips.hidden = !chips.children.length;
      if (chips.children.length) { const clear = document.createElement('button'); clear.type = 'button'; clear.dataset.wwClear = ''; clear.textContent = 'Clear all'; chips.append(clear); }
      const pagination = this.querySelector('.ww-pagination'); pagination.replaceChildren(); pagination.hidden = pages <= 1;
      const addPage = (page, label, current = false) => {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = label; button.setAttribute('aria-label', `Page ${page}`);
        if (current) button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => { this.page = page; this.render(false); this.querySelector('.ww-shopping').scrollIntoView({behavior: this.motion()}); }); pagination.append(button);
      };
      if (this.page > 1) addPage(this.page - 1, '←');
      for (let page = Math.max(1, this.page - 2); page <= Math.min(pages, this.page + 2); page++) addPage(page, String(page), page === this.page);
      if (this.page < pages) addPage(this.page + 1, '→');
      if (updateURL) {
        const url = new URL(location.href);
        Object.entries(state).forEach(([key,value]) => { value ? url.searchParams.set(`ww_${key}`, value === true ? '1' : value) : url.searchParams.delete(`ww_${key}`); });
        this.page > 1 ? url.searchParams.set('page', this.page) : url.searchParams.delete('page');
        history.pushState({}, '', url);
      }
    }
    bindCart() {
      this.addEventListener('submit', async event => {
        const form = event.target.closest('.ww-add-form');
        if (!form) return;
        event.preventDefault();
        if (form.dataset.busy) return;
        form.dataset.busy = 'true';
        const button = form.querySelector('[type=submit]');
        const message = form.querySelector('.ww-cart-message');
        const original = button.textContent;
        button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = 'Adding…'; message.hidden = true;
        try {
          const data = new FormData(form);
          data.set('sections', 'cart-icon-bubble');
          data.set('sections_url', location.pathname);
          const base = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${base}cart/add.js`, {method:'POST', headers:{'X-Requested-With':'XMLHttpRequest', Accept:'application/json'}, body:data});
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'This item could not be added. Please try again.');
          message.textContent = 'Added to your cart.'; message.hidden = false;
          const bubble = document.getElementById('cart-icon-bubble');
          const bubbleHTML = result.sections?.['cart-icon-bubble'];
          if (bubble && bubbleHTML) {
            const section = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
            if (section) bubble.innerHTML = section.innerHTML;
          }
        } catch (error) { message.textContent = error.message === 'Failed to fetch' ? 'Connection interrupted. Please check your cart before trying again.' : error.message; message.hidden = false; }
        finally { delete form.dataset.busy; button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = original; }
      });
    }
  });
}
